/* global axios */
import { DataManager } from '../helper/CacheHelper/DataManager';
import ApiClient from './ApiClient';

class CacheEnabledApiClient extends ApiClient {
  constructor(resource, options = {}) {
    super(resource, options);
    this.accountDataManager = null;
  }

  // These clients are module level singletons, so they are constructed before the router
  // has settled on an account: a boot at /app redirects to /app/accounts/:id without
  // re-evaluating the bundle. Resolving the store once in the constructor therefore pointed
  // every account at a single `wasel-store-` database, where a shared default cache key could
  // hand one account another's rows. The account has to come from the route at read time.
  get dataManager() {
    const accountId = this.accountIdFromRoute;

    if (this.accountDataManager?.accountId !== accountId) {
      this.accountDataManager = new DataManager(accountId);
    }

    return this.accountDataManager;
  }

  // eslint-disable-next-line class-methods-use-this
  get cacheModelName() {
    throw new Error('cacheModelName is not defined');
  }

  get(cache = false) {
    if (cache) {
      return this.getFromCache();
    }

    return this.getFromNetwork();
  }

  getFromNetwork(url = this.url) {
    return axios.get(url, { timeout: 15000 });
  }

  // eslint-disable-next-line class-methods-use-this
  extractDataFromResponse(response) {
    return response.data.payload;
  }

  // eslint-disable-next-line class-methods-use-this
  marshallData(dataToParse) {
    return { data: { payload: dataToParse } };
  }

  async getFromCache() {
    const manager = this.dataManager;
    const url = this.url;
    const accountId = this.accountIdFromRoute;
    try {
      // IDB is not supported in Firefox private mode: https://bugzilla.mozilla.org/show_bug.cgi?id=781982
      await manager.initDb();
    } catch {
      return this.getFromNetwork(url);
    }

    let cacheKeyFromApi;
    try {
      const { data } = await axios.get(
        `/api/v1/accounts/${accountId}/cache_keys`,
        { timeout: 15000 }
      );
      cacheKeyFromApi = data.cache_keys[this.cacheModelName];
      if (await this.validateCacheKey(cacheKeyFromApi, manager)) {
        const localData = await manager.get({
          modelName: this.cacheModelName,
        });
        // An empty list is a valid cached result, not a reason to keep fetching.
        return this.marshallData(localData);
      }
    } catch {
      // Cache metadata/read failures must not trap the page in a loading state.
    }
    return this.refetchAndCommit(cacheKeyFromApi, manager, url);
  }

  async refetchAndCommit(
    newKey = null,
    manager = this.dataManager,
    url = this.url
  ) {
    const response = await this.getFromNetwork(url);

    try {
      await manager.initDb();

      await manager.replace({
        modelName: this.cacheModelName,
        data: this.extractDataFromResponse(response),
      });

      await manager.setCacheKeys({
        [this.cacheModelName]: newKey,
      });
    } catch {
      // Ignore error
    }

    return response;
  }

  async validateCacheKey(cacheKeyFromApi, manager = this.dataManager) {
    if (!manager.db) {
      await manager.initDb();
    }

    const cachekey = await manager.getCacheKey(this.cacheModelName);
    return cacheKeyFromApi != null && cacheKeyFromApi === cachekey;
  }
}

export default CacheEnabledApiClient;
